// React and related imports
import React, { useState } from 'react';

// Custom components
import { QuestionModal } from '../components/QuestionModal/QuestionModal';
import { Loading } from '../components/Shared/Loading/Loading';
import { PageHeader } from '../components/Shared/PageHeader';

// i18 hooks
import { useTranslation } from 'react-i18next';

// Constants
import { INITIAL_Question_STATE } from '../common/constants/initialQuestionState';

// RTK Query hooks
import { useFetchQuestionsQuery } from '../redux/api/flowApi';

// Interfaces
import { IQuestion } from '../common/interfaces/Question.interface';

// Utils
import { QuestionsTable } from '../components/QuestionsTable/QuestionsTable';

// Custom Hooks
import { usePageTitle } from '../hooks/usePageTitle';
import { usePagination } from '../hooks/usePagination';

// Custom components
import { CustomPagination } from '../components/Shared/CustomPagination/CustomPagination';

export const QuestionsPage: React.FC = () => {
  const { t } = useTranslation();

  usePageTitle(t('pageTitles.questions'));

  const [showModal, setShowModal] = useState(false);
  const [isModalForEdit, setIsModalForEdit] = useState(false);
  const [selectedQuestionForEdit, setSelectedQuestionForEdit] =
    useState<IQuestion>(INITIAL_Question_STATE);

  const { currentPage, setCurrentPage, size } = usePagination();

  const { data: response, isLoading } = useFetchQuestionsQuery({
    page: currentPage,
    size,
  });

  const questionsResponse = response?.data.data || [];

  const handleEditQuestion = (question: IQuestion) => {
    setShowModal(true);
    setIsModalForEdit(true);
    setSelectedQuestionForEdit(question);
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <div className="p-6">
      <PageHeader
        title={t('content.questions')}
        buttonText={t('content.addQuestion')}
        buttonOnClick={() => {
          setShowModal(true);
          setIsModalForEdit(false);
        }}
      />
      <QuestionsTable
        questionsList={questionsResponse}
        handleEditQuestion={handleEditQuestion}
      />
      <CustomPagination
        currentPage={currentPage}
        onPageChange={setCurrentPage}
        totalPages={response?.data?.meta?.pageCount ?? 1}
      />
      {showModal && (
        <QuestionModal
          isModalForAdd={!isModalForEdit}
          question={
            isModalForEdit ? selectedQuestionForEdit : INITIAL_Question_STATE
          }
          show={showModal}
          onClose={() => setShowModal(false)}
        />
      )}
    </div>
  );
};
